(() => {
  const KEY = 'remanx_recent_parts_v2';
  const ROOT_SELECTOR = '[data-rmx-recently-viewed]';

  const parseJSON = (value, fallback = []) => {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const readHistory = () => {
    const list = parseJSON(localStorage.getItem(KEY), []);
    return Array.isArray(list) ? list : [];
  };

  const writeHistory = (list) => {
    localStorage.setItem(KEY, JSON.stringify(Array.isArray(list) ? list.slice(0, 20) : []));
    document.dispatchEvent(new CustomEvent('remanx:recently-viewed-change'));
  };

  const text = (value) => String(value ?? '').trim();

  const normaliseYearRange = (yearRange, allYears) => {
    const direct = text(yearRange);
    if (direct) return direct;

    const values = text(allYears)
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    if (!values.length) return '';
    if (values.length === 1) return String(values[0]);
    return `${values[0]}–${values[values.length - 1]}`;
  };

  const safeUrl = (value) => {
    try {
      const url = new URL(text(value) || '#', window.location.origin);
      if (url.protocol === 'http:' || url.protocol === 'https:') return url.href;
    } catch (_error) {}
    return '#';
  };

  const safeImageUrl = (value) => {
    const url = safeUrl(value);
    return url === '#' ? '' : url;
  };

  const money = (cents, currency, locale) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(locale || 'en-GB', {
        style: 'currency',
        currency: text(currency) || 'GBP'
      }).format(amount / 100);
    } catch (_error) {
      return `£${(amount / 100).toFixed(2)}`;
    }
  };

  const createSvg = (pathMarkup, viewBox = '0 0 24 24') => {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = pathMarkup;
    return svg;
  };

  const arrowIcon = () => createSvg('<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>');
  const carIcon = () => createSvg('<path d="M5 16h14l-1-5-3-4H9l-3 4-1 5Z"/><path d="M7 16v2M17 16v2M8 12h8"/>');
  const imageIcon = () => createSvg('<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.5"/><path d="m21 15-5-5L5 20"/>');
  const closeIcon = () => createSvg('<path d="m7 7 10 10M17 7 7 17"/>');

  const historyKey = (item) => text(item?.handle || item?.url);

  const uniqueHistory = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = historyKey(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fitmentText = (item, fallback) => {
    const make = text(item.make);
    const model = text(item.model);
    const yearRange = normaliseYearRange(item.yearRange, item.allYears);
    const vehicle = [make, model].filter(Boolean).join(' ');
    const parts = [vehicle, yearRange].filter(Boolean);
    return parts.join(' · ') || text(fallback);
  };

  const createCard = (item, root) => {
    const li = document.createElement('li');
    li.className = 'rmx-rv__item';

    const article = document.createElement('article');
    article.className = 'rmx-rv__card';

    const link = document.createElement('a');
    link.className = 'rmx-rv__card-link';
    link.href = safeUrl(item.url);

    const media = document.createElement('div');
    media.className = 'rmx-rv__media';

    const imageUrl = safeImageUrl(item.image);
    if (imageUrl) {
      const frame = document.createElement('span');
      frame.className = 'rmx-rv__image-frame';
      frame.setAttribute('aria-hidden', 'true');

      const image = document.createElement('img');
      image.className = 'rmx-rv__image';
      image.src = imageUrl;
      image.alt = text(item.title);
      image.loading = 'lazy';
      image.decoding = 'async';

      frame.appendChild(image);
      media.appendChild(frame);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'rmx-rv__image-placeholder';
      placeholder.appendChild(imageIcon());
      media.appendChild(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'rmx-rv__body';

    const category = text(item.category);
    if (root.dataset.showCategory === 'true' && category) {
      const categoryEl = document.createElement('span');
      categoryEl.className = 'rmx-rv__category';
      categoryEl.textContent = category;
      body.appendChild(categoryEl);
    }

    const title = document.createElement('h3');
    title.className = 'rmx-rv__name';
    title.textContent = text(item.title) || root.dataset.fallbackTitle || 'Repair service';
    body.appendChild(title);

    if (root.dataset.showFitment === 'true') {
      const fitmentValue = fitmentText(item, '');
      if (fitmentValue) {
        const fitment = document.createElement('div');
        fitment.className = 'rmx-rv__fitment';
        fitment.appendChild(carIcon());
        const fitmentCopy = document.createElement('span');
        fitmentCopy.textContent = fitmentValue;
        fitment.appendChild(fitmentCopy);
        body.appendChild(fitment);
      }
    }

    const footer = document.createElement('div');
    footer.className = 'rmx-rv__footer';

    if (root.dataset.showPrice === 'true') {
      const formattedPrice = money(item.price, item.currency, root.dataset.locale);
      if (formattedPrice) {
        const priceWrap = document.createElement('span');
        priceWrap.className = 'rmx-rv__price-wrap';

        const prefix = text(root.dataset.pricePrefix);
        if (prefix) {
          const prefixEl = document.createElement('span');
          prefixEl.className = 'rmx-rv__price-prefix';
          prefixEl.textContent = prefix;
          priceWrap.appendChild(prefixEl);
        }

        const price = document.createElement('span');
        price.className = 'rmx-rv__price';
        price.textContent = formattedPrice;
        priceWrap.appendChild(price);
        footer.appendChild(priceWrap);
      }
    }

    const view = document.createElement('span');
    view.className = 'rmx-rv__view';
    view.textContent = root.dataset.viewLabel || 'View service';
    view.appendChild(arrowIcon());
    footer.appendChild(view);

    body.appendChild(footer);
    link.append(media, body);
    article.appendChild(link);

    if (root.dataset.showRemove === 'true') {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'rmx-rv__remove';
      remove.dataset.rmxRvRemove = historyKey(item);
      remove.setAttribute('aria-label', `${root.dataset.removeLabel || 'Remove'} ${text(item.title)}`.trim());
      remove.appendChild(closeIcon());
      article.appendChild(remove);
    }

    li.appendChild(article);
    return li;
  };

  const updateNavigation = (root) => {
    const viewport = root.querySelector('[data-rmx-rv-viewport]');
    const nav = root.querySelector('[data-rmx-rv-nav]');
    const previous = root.querySelector('[data-rmx-rv-prev]');
    const next = root.querySelector('[data-rmx-rv-next]');
    if (!viewport || !nav || !previous || !next) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    nav.hidden = maxScroll < 8;
    previous.disabled = viewport.scrollLeft <= 4;
    next.disabled = viewport.scrollLeft >= maxScroll - 4;
  };

  const render = (root) => {
    const listElement = root.querySelector('[data-rmx-rv-list]');
    const emptyElement = root.querySelector('[data-rmx-rv-empty]');
    const actionElements = root.querySelectorAll('[data-rmx-rv-history-action]');
    if (!listElement || !emptyElement) return;

    const limit = Math.max(1, Number.parseInt(root.dataset.limit || '4', 10));
    const excludedHandle = text(root.dataset.excludeHandle);
    const excludedHandles = new Set(
      text(root.dataset.excludedHandles)
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean)
    );
    if (excludedHandle) excludedHandles.add(excludedHandle);

    const items = uniqueHistory(readHistory())
      .filter((item) => !excludedHandles.has(text(item.handle)))
      .slice(0, limit);

    listElement.replaceChildren();

    if (!items.length) {
      if (root.dataset.hideEmpty === 'true') {
        root.hidden = true;
      } else {
        root.hidden = false;
        emptyElement.hidden = false;
        actionElements.forEach((element) => { element.hidden = true; });
      }
      updateNavigation(root);
      return;
    }

    root.hidden = false;
    emptyElement.hidden = true;
    actionElements.forEach((element) => { element.hidden = false; });

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(createCard(item, root)));
    listElement.appendChild(fragment);

    requestAnimationFrame(() => updateNavigation(root));
  };

  const scrollViewport = (root, direction) => {
    const viewport = root.querySelector('[data-rmx-rv-viewport]');
    if (!viewport) return;
    const distance = Math.max(220, viewport.clientWidth * 0.84);
    viewport.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  const initialise = (root) => {
    if (!root || root.dataset.rmxRvInitialised === 'true') return;
    root.dataset.rmxRvInitialised = 'true';

    root.addEventListener('click', (event) => {
      const clear = event.target.closest('[data-rmx-rv-clear]');
      if (clear) {
        event.preventDefault();
        writeHistory([]);
        return;
      }

      const remove = event.target.closest('[data-rmx-rv-remove]');
      if (remove) {
        event.preventDefault();
        event.stopPropagation();
        const key = text(remove.dataset.rmxRvRemove);
        writeHistory(readHistory().filter((item) => historyKey(item) !== key));
        return;
      }

      if (event.target.closest('[data-rmx-rv-prev]')) scrollViewport(root, -1);
      if (event.target.closest('[data-rmx-rv-next]')) scrollViewport(root, 1);
    });

    const viewport = root.querySelector('[data-rmx-rv-viewport]');
    if (viewport) {
      let frame = 0;
      viewport.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => updateNavigation(root));
      }, { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => updateNavigation(root));
        observer.observe(viewport);
      } else {
        window.addEventListener('resize', () => updateNavigation(root), { passive: true });
      }
    }

    document.addEventListener('remanx:recently-viewed-change', () => render(root));
    render(root);
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialise(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
